/**
 * 업무 화면 안쪽 사이드바(L7) — 잔디 왼쪽 메뉴 자리(폭 240).
 *
 * 게이트웨이 사이드바(AppSidebar)에는 넣지 않는다(마스터 10-06). 위 = 전역 메뉴(GLOBAL_ITEMS ·
 * 화면이 생길 때마다 한 줄씩 늘린다) · 아래 = «프로젝트» 섹션(머리 sticky · «+» 로 새 프로젝트).
 * 🔴 프로젝트 항목은 <button> 이 아니라 react-router <Link> 다 — 화면 시험·e2e 가 '버튼' 을 세므로
 *    여기서 버튼을 늘리면 그 단언이 흔들린다(링크라 Ctrl·⌘ 클릭 새 탭도 브라우저가 처리한다).
 *    예외: `onNewProject` 가 있을 때 «+» 버튼 하나(새 프로젝트).
 * 🔴 다시 부를 때 직전 목록을 비우지 않는다 — 프로젝트를 옮길 때마다 목록이 깜빡이지 않게.
 */
import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { taskPmAPI } from '../../api/client';
import { errorText } from './errorText';
import { COLORS, RADIUS, btn } from '../../utils/uiStyles';

const DEFAULT_COLOR = '#4C7DD9';     // TaskPmPage 의 기본 프로젝트 색과 같은 값
// 잔디 순서: 홈·내 업무·캘린더·간트차트·노트 — 화면이 생긴 것만 둔다(J4d·J5b3 가 한 줄씩 더한다).
const GLOBAL_ITEMS = [
  { to: '/tasks', label: '전체 프로젝트', end: true },
];

const lineStyle = { padding: '2px 24px 4px', fontSize: '12px' };

export default function TaskPmNav({ currentProjectId, refreshKey, onNewProject }) {
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  // 프로젝트를 옮기거나(currentProjectId) 설정에서 이름·색을 고치면(refreshKey) 다시 읽는다.
  useEffect(() => {
    let alive = true;
    taskPmAPI.listProjects()
      .then((res) => {
        if (!alive) return;                                 // 늦게 온 응답은 버린다
        const rows = Array.isArray(res?.data) ? res.data : [];
        setProjects(rows.filter((p) => p.is_member === true));
        setError('');
        setLoaded(true);
      })
      .catch((e) => {
        if (!alive) return;
        setError(errorText(e, '프로젝트를 불러오지 못했습니다.'));
        setLoaded(true);
      });
    return () => { alive = false; };
  }, [currentProjectId, refreshKey]);

  // 전역 메뉴 한 줄(잔디 41) — 활성 판정·aria-current="page" 는 NavLink 가 붙인다.
  const globalStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '41px',
    margin: '0 12px 2px',
    padding: '0 12px',
    borderRadius: RADIUS.md,
    fontSize: '14px',
    fontWeight: active ? 700 : 600,
    background: active ? COLORS.primarySoft : 'transparent',
    color: active ? COLORS.selectedText : '#4b5563',
    textDecoration: 'none',
    boxSizing: 'border-box',
  });

  // 프로젝트 한 줄(잔디 38).
  const itemStyle = (active, closed) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '38px',
    margin: '0 12px 2px',
    padding: '0 12px',
    borderRadius: RADIUS.md,
    fontSize: '13px',
    fontWeight: active ? 700 : 600,
    background: active ? COLORS.primarySoft : 'transparent',
    color: active ? COLORS.selectedText : (closed ? COLORS.textMuted : '#4b5563'),
    textDecoration: 'none',
    boxSizing: 'border-box',
    // 다시 읽기에 실패하면 남아 있는 목록은 낡은 것이다 → 흐리게 해서 그 사실을 드러낸다.
    opacity: error ? 0.5 : 1,
  });

  return (
    <aside aria-label="업무 프로젝트 메뉴"
      style={{
        width: '240px', flexShrink: 0, boxSizing: 'border-box', height: '100%', overflowY: 'auto',
        background: COLORS.surface, borderRight: `1px solid ${COLORS.border}`, padding: '12px 0',
      }}>
      <nav aria-label="업무 메뉴">
        {GLOBAL_ITEMS.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end}
            style={({ isActive }) => globalStyle(isActive)}>{item.label}</NavLink>
        ))}
      </nav>

      <nav aria-label="내 프로젝트" style={{ marginTop: '12px' }}>
        {/* 🔴 머리는 sticky — 프로젝트가 많아 목록을 스크롤해도 «프로젝트» 와 «+» 가 위에 붙어 있다. */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 1, background: COLORS.surface,
          display: 'flex', alignItems: 'center', height: '36px', padding: '0 12px 0 24px',
        }}>
          <span style={{ fontSize: '12px', fontWeight: 700, color: COLORS.textSub, letterSpacing: '.04em' }}>프로젝트</span>
          {/* «+» = 시안 F 아이콘 단추(btn('icon') 28×28 · r5 · 흐림). hover 바탕은 공용 CSS(.gw-btn--icon · public/index.html)가 맡는다. */}
          {onNewProject && (
            <button type="button" aria-label="새 프로젝트" title="새 프로젝트" onClick={onNewProject}
              className="gw-btn gw-btn--icon"
              style={{ ...btn('icon'), marginLeft: 'auto', boxSizing: 'border-box', padding: 0, fontSize: '16px', lineHeight: 1 }}>+</button>
          )}
        </div>

        {/* 🔴 오류는 목록 위에 둔다 — 아래에 두면 낡은 목록이 먼저 보여 최신인 줄 안다. */}
        {error && <div style={{ ...lineStyle, color: COLORS.dangerText }}>{error}</div>}

        {projects.map((p) => {
          const active = String(p.id) === String(currentProjectId);
          return (
            <Link
              key={p.id}
              to={`/tasks/${p.id}`}
              // 🔴 'page' 가 아니다 — 위 전역 메뉴(NavLink)가 aria-current="page" 를 쓴다.
              aria-current={active ? 'true' : undefined}
              onMouseEnter={active ? undefined : (e) => { e.currentTarget.style.background = COLORS.primarySoft; }}
              onMouseLeave={active ? undefined : (e) => { e.currentTarget.style.background = 'transparent'; }}
              style={itemStyle(active, p.closed)}
            >
              <span aria-hidden="true" style={{ width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, background: p.color || DEFAULT_COLOR }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
            </Link>
          );
        })}

        {!error && loaded && projects.length === 0 && (
          <div style={{ ...lineStyle, color: COLORS.textMuted }}>참여 중인 프로젝트가 없습니다.</div>
        )}
      </nav>
    </aside>
  );
}
